import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {LABELS} from '../constants/labels'
import type {IssueFieldAddedEvent$key} from './__generated__/IssueFieldAddedEvent.graphql'
import type {IssueFieldChangedEvent$key} from './__generated__/IssueFieldChangedEvent.graphql'
import type {IssueFieldRemovedEvent$key} from './__generated__/IssueFieldRemovedEvent.graphql'
import {IssueFieldEvent, type IssueFieldEventBaseProps} from './IssueFieldEvent'
import {RolledupIssueFieldEvent} from './RolledupIssueFieldEvent'

type IssueFieldEventKey = IssueFieldAddedEvent$key | IssueFieldChangedEvent$key | IssueFieldRemovedEvent$key

type IssueFieldRemovedEventProps = IssueFieldEventBaseProps & {
  queryRef: IssueFieldRemovedEvent$key & {createdAt?: string}
  rollupGroup?: Record<string, IssueFieldEventKey[]>
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const IssueFieldRemovedEventFragment = graphql`
  fragment IssueFieldRemovedEvent on IssueFieldRemovedEvent {
    databaseId
    actor {
      # eslint-disable-next-line relay/must-colocate-fragment-spreads
      ...TimelineRowEventActor
    }
    createdAt
    issueField {
      ... on IssueFieldText {
        dataType
        name
      }
      ... on IssueFieldDate {
        name
        dataType
      }
      ... on IssueFieldNumber {
        name
        dataType
      }
      ... on IssueFieldSingleSelect {
        name
        dataType
      }
      ... on IssueFieldMultiSelect {
        name
        dataType
      }
    }
    intent {
      rationale
    }
  }
`

export function IssueFieldRemovedEvent({queryRef, rollupGroup, ...props}: IssueFieldRemovedEventProps) {
  const {actor, createdAt, issueField, databaseId, intent} = useFragment(IssueFieldRemovedEventFragment, queryRef)

  const rollupContent = rollupGroup ? (
    <RolledupIssueFieldEvent rollupGroup={rollupGroup} repositoryNameWithOwner={props.repositoryNameWithOwner} />
  ) : undefined

  return (
    <IssueFieldEvent
      actor={actor}
      createdAt={createdAt}
      fieldName={issueField?.name}
      dataType={issueField?.dataType}
      databaseId={databaseId}
      leadingLabel={rollupGroup ? undefined : LABELS.timeline.issueFieldRemoved.leading}
      hasValue={false}
      rollupContent={rollupContent}
      rationale={rollupGroup ? undefined : (intent?.rationale ?? undefined)}
      analyticsAction="ISSUE_FIELD_REMOVED"
      {...props}
    />
  )
}
